import { useState } from 'react';
import { toast } from 'sonner';
import { useTier } from '../../context/TierContext';
import { UpgradePrompt } from '../../components/FeatureGate';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { ConfirmationDialog } from '../../components/ui/confirmation-dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Pencil, Trash2, Search, MoreHorizontal, Shield } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const ComplianceTypesTab = ({ api, complianceTypes, fetchComplianceTypes }) => {
  const { hasFeature } = useTier();
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    is_active: true,
    applies_to: ['operator'],
    requires_expiry_date: false
  });

  // Drawer state for sub-types
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', onConfirm: null });
  const [selectedType, setSelectedType] = useState(null);
  const [subTypeDialogOpen, setSubTypeDialogOpen] = useState(false);
  const [editingSubType, setEditingSubType] = useState(null);
  const [subTypeForm, setSubTypeForm] = useState({
    code: '',
    name: '',
    description: ''
  });

  const activeCount = complianceTypes.filter(t => t.is_active).length;
  const customCount = complianceTypes.filter(t => !t.is_system).length;

  const filteredTypes = complianceTypes.filter(type =>
    type.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setForm({ name: '', description: '', is_active: true, applies_to: ['operator'], requires_expiry_date: false, mandatory_for_workers: false });
    setEditingType(null);
  };

  const handleOpenDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setForm({
        name: type.name,
        description: type.description || '',
        is_active: type.is_active,
        applies_to: type.applies_to || ['operator'],
        requires_expiry_date: type.requires_expiry_date || false,
        mandatory_for_workers: type.mandatory_for_workers || false,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingType) {
        await api.put(`/compliance-types/${editingType.id}`, form);
        toast.success('Compliance type updated');
      } else {
        await api.post('/compliance-types', form);
        toast.success('Compliance type created');
      }
      setDialogOpen(false);
      resetForm();
      fetchComplianceTypes();
      refreshSelectedType();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (type) => {
    if (type.is_system) {
      toast.error('System types cannot be deleted');
      return;
    }
    setConfirmDialog({
      open: true,
      title: 'Delete Compliance Type',
      description: `Delete compliance type "${type.name}"?`,
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        try {
          await api.delete(`/compliance-types/${type.id}`);
          toast.success('Compliance type deleted');
          if (selectedType?.id === type.id) {
            setDrawerOpen(false);
            setSelectedType(null);
          }
          fetchComplianceTypes();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Delete failed');
        }
      },
    });
  };

  const handleToggleActive = async (type) => {
    try {
      await api.put(`/compliance-types/${type.id}`, {
        name: type.name,
        description: type.description,
        is_active: !type.is_active,
        applies_to: type.applies_to || ['operator'],
        requires_expiry_date: type.requires_expiry_date || false,
        mandatory_for_workers: type.mandatory_for_workers || false,
        sub_types: type.sub_types || []
      });
      toast.success(`Compliance type ${type.is_active ? 'deactivated' : 'activated'}`);
      fetchComplianceTypes();
      refreshSelectedType();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  const handleOpenDrawer = (type) => {
    setSelectedType(type);
    setDrawerOpen(true);
  };

  const refreshSelectedType = async () => {
    if (selectedType) {
      try {
        const response = await api.get('/compliance-types/all');
        const updated = response.data.find(t => t.id === selectedType.id);
        if (updated) setSelectedType(updated);
      } catch (error) {
        console.error('Failed to refresh compliance type:', error);
      }
    }
  };

  // Sub-type handlers
  const resetSubTypeForm = () => {
    setSubTypeForm({ code: '', name: '', description: '' });
    setEditingSubType(null);
  };

  const handleOpenSubTypeDialog = (subType = null) => {
    if (subType) {
      setEditingSubType(subType);
      setSubTypeForm({
        code: subType.code,
        name: subType.name,
        description: subType.description || ''
      });
    } else {
      resetSubTypeForm();
    }
    setSubTypeDialogOpen(true);
  };

  const handleSubTypeSubmit = async (e) => {
    e.preventDefault();
    if (!selectedType) return;
    
    try {
      if (editingSubType) {
        await api.put(`/compliance-types/${selectedType.id}/sub-types/${editingSubType.id}`, {
          ...subTypeForm,
          id: editingSubType.id
        });
        toast.success('Class updated');
      } else {
        await api.post(`/compliance-types/${selectedType.id}/sub-types`, subTypeForm);
        toast.success('Class added');
      }
      setSubTypeDialogOpen(false);
      resetSubTypeForm();
      fetchComplianceTypes();
      refreshSelectedType();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleSubTypeDelete = async (subType) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Class',
      description: `Delete class "${subType.code} - ${subType.name}"?`,
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        try {
          await api.delete(`/compliance-types/${selectedType.id}/sub-types/${subType.id}`);
          toast.success('Class deleted');
          fetchComplianceTypes();
          refreshSelectedType();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Delete failed');
        }
      },
    });
  };

  if (!hasFeature('compliance')) {
    return (
      <UpgradePrompt 
        feature="compliance"
        title="Compliance Types"
        description="Manage compliance types for tracking operator certifications, licenses, and equipment inspections. Available on Advanced plan and above."
      />
    );
  }

  return (
    <>
      <Card className="card-industrial">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="font-heading">Compliance Types</CardTitle>
              <CardDescription>
                {activeCount} active types, {customCount} custom types
              </CardDescription>
            </div>
            <Button className="btn-volt" onClick={() => handleOpenDialog()} data-testid="add-compliance-type-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Type
            </Button>
          </div>
          <div className="relative max-w-sm mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="compliance-type-search"
              placeholder="Search types..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="label-overline">Name</TableHead>
                  <TableHead className="label-overline">Description</TableHead>
                  <TableHead className="label-overline">Applies To</TableHead>
                  <TableHead className="label-overline text-center">Classes</TableHead>
                  <TableHead className="label-overline">Type</TableHead>
                  <TableHead className="label-overline">Status</TableHead>
                  <TableHead className="label-overline text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTypes.map((type) => (
                  <TableRow 
                    key={type.id} 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleOpenDrawer(type)}
                  >
                    <TableCell className="font-medium">{type.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {type.description || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(type.applies_to || ['operator']).map(entity => (
                          <Badge key={entity} variant="outline" className="text-xs capitalize">
                            {entity}
                          </Badge>
                        ))}
                        {type.mandatory_for_workers && (
                          <Badge className="bg-red-500/15 text-red-500 border-red-500/30 text-[10px]">
                            Mandatory
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{type.sub_types?.length || 0}</Badge>
                    </TableCell>
                    <TableCell>
                      {type.is_system ? (
                        <Badge variant="secondary">System</Badge>
                      ) : (
                        <Badge className="bg-[#ffd108]/20 text-[#ffd108]">Custom</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <Switch
                          checked={type.is_active}
                          onCheckedChange={() => handleToggleActive(type)}
                        />
                        <span className={type.is_active ? 'text-green-500' : 'text-muted-foreground'}>
                          {type.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenDialog(type); }}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit Details
                          </DropdownMenuItem>
                          {!type.is_system && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={(e) => { e.stopPropagation(); handleDelete(type); }}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {filteredTypes.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No compliance types found
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Compliance Type Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingType ? 'Edit Compliance Type' : 'Add Compliance Type'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Name <span className="text-red-500">*</span></Label>
              <Input
                data-testid="compliance-type-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Dogging Licence"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description of this compliance type"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Applies To <span className="text-red-500">*</span></Label>
              <p className="text-xs text-muted-foreground mb-2">
                Select which entity types this compliance can be assigned to
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="applies-operator"
                    checked={form.applies_to?.includes('operator')}
                    onCheckedChange={(checked) => {
                      const current = form.applies_to || [];
                      setForm({
                        ...form,
                        applies_to: checked 
                          ? [...current, 'operator']
                          : current.filter(t => t !== 'operator')
                      });
                    }}
                  />
                  <Label htmlFor="applies-operator" className="cursor-pointer">Operators</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="applies-equipment"
                    checked={form.applies_to?.includes('equipment')}
                    onCheckedChange={(checked) => {
                      const current = form.applies_to || [];
                      setForm({
                        ...form,
                        applies_to: checked 
                          ? [...current, 'equipment']
                          : current.filter(t => t !== 'equipment')
                      });
                    }}
                  />
                  <Label htmlFor="applies-equipment" className="cursor-pointer">Assets</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="applies-supplier"
                    checked={form.applies_to?.includes('supplier')}
                    onCheckedChange={(checked) => {
                      const current = form.applies_to || [];
                      setForm({
                        ...form,
                        applies_to: checked 
                          ? [...current, 'supplier']
                          : current.filter(t => t !== 'supplier')
                      });
                    }}
                  />
                  <Label htmlFor="applies-supplier" className="cursor-pointer">Suppliers</Label>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-md border p-3">
              <Switch
                checked={form.requires_expiry_date}
                onCheckedChange={(checked) => setForm({ ...form, requires_expiry_date: checked })}
                data-testid="compliance-type-requires-expiry-switch"
              />
              <div className="space-y-0.5">
                <Label>Requires Expiry Date</Label>
                <p className="text-xs text-muted-foreground">
                  When enabled, adding a compliance record of this type will require an expiry date.
                </p>
              </div>
            </div>
            {form.applies_to?.includes('operator') && (
              <div className="flex items-start gap-3 rounded-md border border-red-500/20 bg-red-500/5 p-3">
                <Switch
                  checked={form.mandatory_for_workers}
                  onCheckedChange={(checked) => setForm({ ...form, mandatory_for_workers: checked })}
                  data-testid="compliance-type-mandatory-switch"
                />
                <div className="space-y-0.5">
                  <Label className="flex items-center gap-1.5">
                    Mandatory for All Workers
                    <Badge className="bg-red-500/15 text-red-500 border-red-500/30 text-[10px]">Onboarding</Badge>
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Workers cannot be allocated to <strong>any</strong> job until they hold a valid record of this compliance type.
                    Use for onboarding requirements like White Card or General Induction.
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
              <Label>Active</Label>
            </div>
            {editingType?.is_system && (
              <p className="text-xs text-muted-foreground">
                This is a system type. You can edit it or deactivate it, but it cannot be deleted.
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt">
                {editingType ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Compliance Type Detail Drawer (for managing sub-types/classes) */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-[700px] sm:max-w-[700px] overflow-y-auto">
          {selectedType && (
            <>
              <SheetHeader className="pb-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Shield className="h-6 w-6 text-[#ffd108]" />
                    <div>
                      <SheetTitle className="font-heading text-xl flex items-center gap-2">
                        {selectedType.name}
                        {selectedType.is_system && (
                          <Badge variant="secondary">System</Badge>
                        )}
                        {!selectedType.is_active && (
                          <Badge variant="destructive">Inactive</Badge>
                        )}
                      </SheetTitle>
                      {selectedType.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {selectedType.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleOpenDialog(selectedType)}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit Details
                  </Button>
                </div>
              </SheetHeader>

              <div className="py-6">
                {/* Sub-types/Classes Section */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-semibold text-lg">Classes / Competencies</h3>
                    <Badge variant="secondary">
                      {selectedType.sub_types?.length || 0}
                    </Badge>
                  </div>
                  <Button 
                    className="btn-volt" 
                    size="sm"
                    onClick={() => handleOpenSubTypeDialog()}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Class
                  </Button>
                </div>

                <p className="text-sm text-muted-foreground mb-4">
                  Define the classes or competencies that can be held under this compliance type. 
                  For example, a High Risk Work Licence can have classes like DG (Dogging), RB (Rigging Basic), etc.
                </p>

                {selectedType.sub_types?.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="label-overline w-[100px]">Code</TableHead>
                          <TableHead className="label-overline">Name</TableHead>
                          <TableHead className="label-overline">Description</TableHead>
                          <TableHead className="label-overline text-right w-[80px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedType.sub_types.map((subType) => (
                          <TableRow key={subType.id}>
                            <TableCell className="font-mono font-semibold text-[#ffd108]">
                              {subType.code}
                            </TableCell>
                            <TableCell className="font-medium">{subType.name}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {subType.description || '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleOpenSubTypeDialog(subType)}>
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => handleSubTypeDelete(subType)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground border rounded-lg">
                    <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No classes defined yet.</p>
                    <button 
                      onClick={() => handleOpenSubTypeDialog()} 
                      className="text-[#ffd108] hover:underline mt-1"
                    >
                      Add your first class
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Add/Edit Sub-Type Dialog */}
      <Dialog open={subTypeDialogOpen} onOpenChange={setSubTypeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingSubType ? 'Edit Class' : 'Add Class'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubTypeSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Code <span className="text-red-500">*</span></Label>
              <Input
                value={subTypeForm.code}
                onChange={(e) => setSubTypeForm({ ...subTypeForm, code: e.target.value.toUpperCase() })}
                placeholder="e.g., DG, RB, CN"
                className="font-mono uppercase"
                maxLength={10}
                required
              />
              <p className="text-xs text-muted-foreground">
                Short code for this class (e.g., DG for Dogging)
              </p>
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Name <span className="text-red-500">*</span></Label>
              <Input
                value={subTypeForm.name}
                onChange={(e) => setSubTypeForm({ ...subTypeForm, name: e.target.value })}
                placeholder="e.g., Dogging, Basic Rigging"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Description</Label>
              <Textarea
                value={subTypeForm.description}
                onChange={(e) => setSubTypeForm({ ...subTypeForm, description: e.target.value })}
                placeholder="Optional description of this class"
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSubTypeDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" disabled={!subTypeForm.code || !subTypeForm.name}>
                {editingSubType ? 'Update' : 'Add Class'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(d => ({ ...d, open }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        confirmText="Delete"
        variant="destructive"
      />
    </>
  );
};

export default ComplianceTypesTab;
